import React, { useEffect, useState } from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { Modal, Select, MenuItem, Chip, Box, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { Typography, CircularProgress, TextField, Button, IconButton, FormControl, InputLabel } from '@mui/material';
import { useLazyQuery, useMutation } from '@apollo/client';
import './modal-edit.scss';
import { DELETE_CLASS_TEMPLATE, GET_CLASS_BY_ID, UPDATE_CLASS_DESCRIPTION, UPDATE_CLASS_TEMPLATE, GENERATE_CALENDARS } from '../../shared/graphql/branch';
import { AddCircleOutline, Delete, Close, Edit, Check, Clear } from '@mui/icons-material';
import { showToast } from '../../shared/utils/toast';

interface ModalDetailedClassProps {
  open: boolean;
  handleClose: () => void;
  classId: number;
}

const dayMap: { [key: number]: string } = {
  1: 'Lunes',
  2: 'Martes',
  3: 'Miércoles',
  4: 'Jueves',
  5: 'Viernes',
  6: 'Sábado',
  0: 'Domingo'
};


const getDayNumber = (dayName: string): number => {
  return parseInt(Object.keys(dayMap).find(key => dayMap[parseInt(key)] === dayName) || '0', 10);
};

const ModalDetailedClass: React.FC<ModalDetailedClassProps> = ({ open, handleClose, classId }) => {
  const [getClassDatadetailed, { data: dataDetailed, loading: loadingDetailed, error: errorDetailed, refetch }] = useLazyQuery(GET_CLASS_BY_ID, {
    variables: { class_id: classId },
    fetchPolicy: 'network-only',
    onCompleted: data => {
      setDescriptionClass(data.classById?.description_html ?? '');
      setTemplates(data.classById?.templates ?? []);
    }
  });

  const [updateClassTemplate] = useMutation(UPDATE_CLASS_TEMPLATE, {
    onCompleted: () => refetch()
  });

  const [updateDescription] = useMutation(UPDATE_CLASS_DESCRIPTION, {
    onCompleted: () => refetch()  
  });

  const [deleteClassTemplate] = useMutation(DELETE_CLASS_TEMPLATE, {
    onCompleted: () => refetch()
  });

  const [generateCalendars] = useMutation(GENERATE_CALENDARS);

  const [descriptionClass, setDescriptionClass] = useState('');
  const [templates, setTemplates] = useState<any[]>([]);
  const [groupedTemplates, setGroupedTemplates] = useState<any[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [deletingIndex, setDeletingIndex] = useState<number | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [originalValues, setOriginalValues] = useState<any>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [applyToAllDays, setApplyToAllDays] = useState(true);

  useEffect(() => {
    if (open) {
      getClassDatadetailed();
    }
  }, [open, getClassDatadetailed]);

  useEffect(() => {
    if (templates.length > 0) {
      const grouped = groupTemplatesBySchedule(templates);
      setGroupedTemplates(grouped);
    } else {
      setGroupedTemplates([]);
    }
  }, [templates]);

  const groupTemplatesBySchedule = (templateList: any[]) => {
    const groups: any[] = [];
    const processed = new Set();

    templateList.forEach((template, index) => {
      if (processed.has(index)) return;

      const matchingTemplates = templateList.filter((t, i) => {
        if (i === index || processed.has(i)) return false;
        return t.startTime === template.startTime &&
               t.endTime === template.endTime &&
               t.capacity === template.capacity &&
               t.enabled === template.enabled &&
               t.holidays === template.holidays &&
               t.date_publication === template.date_publication;
      });

      const allMatchingIndices = [index, ...matchingTemplates.map(t => templateList.indexOf(t))];
      allMatchingIndices.forEach(i => processed.add(i));

      const allDays = [template, ...matchingTemplates].map(t => 
        typeof t.day === 'string' ? parseInt(t.day, 10) : t.day
      ).filter(d => !isNaN(d));

      const templateIds = [template, ...matchingTemplates]
        .map(t => t.id || t.id_template)
        .filter(id => id !== null && id !== undefined);

      groups.push({
        ...template,
        days: allDays,
        templateIds: templateIds,
        isGrouped: templateIds.length > 1,
        date_publication: template.date_publication
      });
    });

    return groups;
  };

  const handleTemplateChange = (index: number, field: string, value: string | string[]) => {
    const updatedGrouped = [...groupedTemplates];
    const groupToUpdate = updatedGrouped[index];

    if (field === 'day' && Array.isArray(value)) {
      const newDays = value.map(day => getDayNumber(day));
      updatedGrouped[index] = { ...groupToUpdate, days: newDays };
    } else if (field === 'day') {
      const newDay = getDayNumber(value as string);
      updatedGrouped[index] = { ...groupToUpdate, days: [newDay] };
    } else {
      updatedGrouped[index] = { ...groupToUpdate, [field]: value };
    }

    setGroupedTemplates(updatedGrouped);
  };
  
  

  const handleSave = async () => {
    const cleanDescription = descriptionClass.replace(/<[^>]*>?/gm, '');
    setIsSaving(true);
    
    try {
      await updateDescription({
        variables: {
          classId,
          description: cleanDescription,
          description_html: descriptionClass
        }
      });

      const templatesToCreate: any[] = [];
      const templatesToDelete: number[] = [];

      groupedTemplates.forEach(group => {
        if (group.date_publication !== null && group.date_publication !== undefined) {
          return;
        }

        const hasExistingTemplates = group.templateIds && group.templateIds.length > 0;
        const selectedDays = Array.isArray(group.days) ? group.days : [group.days];

        if (hasExistingTemplates) {
          templatesToDelete.push(...group.templateIds);
        }

        templatesToCreate.push({
          id_template: null,
          start_time: group.startTime,
          end_time: group.endTime,
          capacity: parseInt(group.capacity, 10),
          days: selectedDays.filter((d: any) => !isNaN(d)),
          enable: group.enabled,
          holidays: group.holidays,
          date_publication: null,
          class_id: classId
        });
      });

      for (const templateId of templatesToDelete) {
        await deleteClassTemplate({
          variables: { classTemplateId: templateId }
        });
      }

      if (templatesToCreate.length > 0) {
        await updateClassTemplate({
          variables: {
            classTemplate: templatesToCreate
          }
        });

        await generateCalendars({
          variables: {
            studioId: null,
            branchId: null,
            classId: classId,
            templateId: null
          }
        });
      }

      const result = await refetch();
      
      if (result.data?.classById?.templates) {
        const updatedTemplates = result.data.classById.templates;
        setTemplates(updatedTemplates);
        const regrouped = groupTemplatesBySchedule(updatedTemplates);
        setGroupedTemplates(regrouped);
      }

      showToast.success('¡Horarios guardados y calendarios generados exitosamente!');
    } catch (error) {
      console.error("Error:", error);
      showToast.error('Error al guardar los horarios. Por favor, intenta de nuevo.');
    } finally {
      setIsSaving(false);
    }
  };

  const newRegister = () => {
    setGroupedTemplates([...groupedTemplates, {
      id_template: null,
      startTime: '',
      endTime: '',
      capacity: '',
      days: [],
      enabled: true,
      holidays: false,
      date_publication: null,
      class_id: classId,
      templateIds: [],
      isGrouped: false
    }]);
  };

  const handleEdit = (index: number) => {
    const group = groupedTemplates[index];
    setEditingIndex(index);
    setOriginalValues({
      startTime: group.startTime,
      endTime: group.endTime
    });
  };

  const handleCancelEdit = (index: number) => {
    if (originalValues) {
      const updatedGroups = [...groupedTemplates];
      updatedGroups[index] = {
        ...updatedGroups[index],
        startTime: originalValues.startTime,
        endTime: originalValues.endTime
      };
      setGroupedTemplates(updatedGroups);
    }
    setEditingIndex(null);
    setOriginalValues(null);
  };

  const handleSaveEdit = (index: number) => {
    const group = groupedTemplates[index];
    
    if (!group.startTime || !group.endTime) {
      showToast.warning('Por favor, completa los horarios');
      return;
    }

    if (group.startTime >= group.endTime) {
      showToast.warning('La hora de fin debe ser posterior a la hora de inicio');
      return;
    }

    setShowConfirmModal(true);
  };

  const handleConfirmSave = async () => {
    if (editingIndex === null) return;

    const group = groupedTemplates[editingIndex];
    setIsSaving(true);
    setShowConfirmModal(false);

    try {
      if (applyToAllDays) {
        for (const templateId of group.templateIds) {
          await deleteClassTemplate({
            variables: { classTemplateId: templateId }
          });
        }

        await updateClassTemplate({
          variables: {
            classTemplate: [{
              id_template: null,
              start_time: group.startTime,
              end_time: group.endTime,
              capacity: parseInt(group.capacity, 10),
              days: Array.isArray(group.days) ? group.days : [group.days],
              enable: group.enabled,
              holidays: group.holidays,
              date_publication: null,
              class_id: classId
            }]
          }
        });

        await generateCalendars({
          variables: {
            studioId: null,
            branchId: null,
            classId: classId,
            templateId: null
          }
        });
      } else {
        if (selectedDay === null) {
          showToast.warning('Por favor, selecciona un día');
          setIsSaving(false);
          return;
        }

        const remainingDays = Array.isArray(group.days) 
          ? group.days.filter((d: number) => d !== selectedDay)
          : [];

        const templateToDelete = group.templateIds.find((_: any, idx: number) => {
          const dayValue = Array.isArray(group.days) ? group.days[idx] : group.days;
          return dayValue === selectedDay;
        });

        if (templateToDelete) {
          await deleteClassTemplate({
            variables: { classTemplateId: templateToDelete }
          });
        }

        await updateClassTemplate({
          variables: {
            classTemplate: [{
              id_template: null,
              start_time: group.startTime,
              end_time: group.endTime,
              capacity: parseInt(group.capacity, 10),
              days: [selectedDay],
              enable: group.enabled,
              holidays: group.holidays,
              date_publication: null,
              class_id: classId
            }]
          }
        });

        if (remainingDays.length > 0) {
          await updateClassTemplate({
            variables: {
              classTemplate: [{
                id_template: null,
                start_time: originalValues.startTime,
                end_time: originalValues.endTime,
                capacity: parseInt(group.capacity, 10),
                days: remainingDays,
                enable: group.enabled,
                holidays: group.holidays,
                date_publication: null,
                class_id: classId
              }]
            }
          });
        }

        await generateCalendars({
          variables: {
            studioId: null,
            branchId: null,
            classId: classId,
            templateId: null
          }
        });
      }

      const result = await refetch();
      
      if (result.data?.classById?.templates) {
        const updatedTemplates = result.data.classById.templates;
        setTemplates(updatedTemplates);
        const regrouped = groupTemplatesBySchedule(updatedTemplates);
        setGroupedTemplates(regrouped);
      }

      showToast.success('¡Horario actualizado y calendarios generados exitosamente!');
      setEditingIndex(null);
      setOriginalValues(null);
      setSelectedDay(null);
      setApplyToAllDays(true);
    } catch (error) {
      console.error("Error:", error);
      showToast.error('Error al actualizar el horario. Por favor, intenta de nuevo.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (index: number) => {
    const groupToDelete = groupedTemplates[index];
    const hasExistingTemplates = groupToDelete.templateIds && groupToDelete.templateIds.length > 0;

    if (!hasExistingTemplates) {
      const updatedGroups = groupedTemplates.filter((_, i) => i !== index);
      setGroupedTemplates(updatedGroups);
      showToast.info('Horario eliminado de la lista');
      return;
    }

    setDeletingIndex(index);
    
    try {
      for (const templateId of groupToDelete.templateIds) {
        await deleteClassTemplate({
          variables: { classTemplateId: templateId }
        });
      }
      
      const result = await refetch();
      
      if (result.data?.classById?.templates) {
        const updatedTemplates = result.data.classById.templates;
        setTemplates(updatedTemplates);
        const regrouped = groupTemplatesBySchedule(updatedTemplates);
        setGroupedTemplates(regrouped);
      }
      
      showToast.success('¡Horario(s) eliminado(s) exitosamente!');
    } catch (error) {
      console.error("Error deleting template:", error);
      showToast.error('Error al eliminar el horario. Por favor, intenta de nuevo.');
    } finally {
      setDeletingIndex(null);
    }
  };

  const editorConfig = {
    toolbar: ['bold', 'italic', 'link'],
  };

  return (
    <>
      <Modal
        open={open}
        onClose={() => {
          if (!isSaving && deletingIndex === null) {
            handleClose();
            refetch(); 
          }
        }}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        <div className="modalStyle">
        <IconButton
          onClick={() => {
            if (!isSaving && deletingIndex === null) {
              handleClose();
            }
          }}
          disabled={isSaving || deletingIndex !== null}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Close />
        </IconButton>
        {loadingDetailed && <CircularProgress />}
        {errorDetailed && <Typography color="error">Error: {errorDetailed.message}</Typography>}
        <div className='wrapperModal'>
          <div className='titleModal'>
            <p><span>Nombre de la clase:</span> {dataDetailed?.classById?.name ?? 'Nombre no disponible'}</p>
            <CKEditor
              editor={ClassicEditor as any} // Agregar 'as any' para evitar errores de tipo
              config={editorConfig}
              data={descriptionClass}
              onReady={editor => {
              }}
              onChange={(event, editor) => {
                const data = editor.getData();
                setDescriptionClass(data);
              }}
            />
          </div>
            <div>
              <p>Horarios</p>
            </div>
          <div className='containerOptionsModal'>
            {groupedTemplates.map((group, index) => (
              <div 
                className="options" 
                key={index}
                style={{
                  border: (group.templateIds && group.templateIds.length > 0 && editingIndex === index) ? '2px solid #1976d2' : undefined,
                  borderRadius: (group.templateIds && group.templateIds.length > 0 && editingIndex === index) ? '8px' : undefined,
                  padding: (group.templateIds && group.templateIds.length > 0 && editingIndex === index) ? '8px' : undefined,
                }}
              >
                <TextField
                  label="Hora de inicio"
                  fullWidth
                  variant="outlined"
                  value={group.startTime}
                  onChange={(e) => handleTemplateChange(index, 'startTime', e.target.value)}
                  disabled={
                    (group.date_publication !== null && group.date_publication !== undefined) ||
                    (group.templateIds && group.templateIds.length > 0 && editingIndex !== index)
                  }
                  InputLabelProps={{
                    shrink: true,
                  }}
                  style={{ width: '20%' }}
                />
                <TextField
                  label="Hora de fin"
                  fullWidth
                  variant="outlined"
                  value={group.endTime}
                  onChange={(e) => handleTemplateChange(index, 'endTime', e.target.value)}
                  disabled={
                    (group.date_publication !== null && group.date_publication !== undefined) ||
                    (group.templateIds && group.templateIds.length > 0 && editingIndex !== index)
                  }
                  InputLabelProps={{
                    shrink: true,
                  }}
                  style={{ width: '20%' }}
                />
                <TextField
                  label="Capacidad"
                  fullWidth
                  variant="outlined"
                  value={group.capacity}
                  onChange={(e) => handleTemplateChange(index, 'capacity', e.target.value)}
                  disabled={
                    (group.date_publication !== null && group.date_publication !== undefined) ||
                    (group.templateIds && group.templateIds.length > 0 ? (editingIndex !== null && editingIndex !== index) : false)
                  }
                  InputLabelProps={{
                    shrink: true,
                  }}
                  style={{ width: '10%' }}
                />
                <Select
                  label="Dia"
                  fullWidth
                  multiple
                  disabled={
                    (group.date_publication !== null && group.date_publication !== undefined) ||
                    (group.templateIds && group.templateIds.length > 0 ? (editingIndex !== null && editingIndex !== index) : false)
                  }
                  value={Array.isArray(group.days) 
                    ? group.days.map((d: number) => dayMap[d]).filter(Boolean)
                    : group.days 
                      ? [dayMap[group.days]].filter(Boolean)
                      : []}
                  onChange={(e) => handleTemplateChange(index, 'day', e.target.value as string[])}
                  displayEmpty
                  inputProps={{ 'aria-label': 'Without label' }}
                  variant="outlined"
                  style={{ width: '30%' }}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as string[]).map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {Object.entries(dayMap).map(([key, name]) => (
                    <MenuItem key={key} value={name}>
                      {name}
                    </MenuItem>
                  ))}
                </Select>

                {group.templateIds && group.templateIds.length > 0 && !(group.date_publication !== null && group.date_publication !== undefined) && (
                  <>
                    {editingIndex === index ? (
                      <>
                        <IconButton 
                          color="success" 
                          onClick={() => handleSaveEdit(index)}
                          disabled={isSaving}
                          title="Guardar cambios"
                        >
                          <Check />
                        </IconButton>
                        <IconButton 
                          color="default" 
                          onClick={() => handleCancelEdit(index)}
                          disabled={isSaving}
                          title="Cancelar"
                        >
                          <Clear />
                        </IconButton>
                      </>
                    ) : (
                      <IconButton 
                        color="primary" 
                        onClick={() => handleEdit(index)}
                        disabled={editingIndex !== null || deletingIndex !== null || isSaving}
                        title="Editar horario"
                      >
                        <Edit />
                      </IconButton>
                    )}
                  </>
                )}

                <IconButton 
                  color="error" 
                  onClick={() => handleDelete(index)}
                  disabled={deletingIndex !== null || isSaving || editingIndex !== null}
                >
                  {deletingIndex === index ? (
                    <CircularProgress size={24} color="error" />
                  ) : (
                    <Delete />
                  )}
                </IconButton>
              </div>
            ))}
          </div>
          <div className='footerModal'>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleSave}
              disabled={isSaving || deletingIndex !== null}
              startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {isSaving ? 'Guardando...' : 'Guardar'}
            </Button>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={newRegister}
              disabled={isSaving || deletingIndex !== null}
            >
              <AddCircleOutline /> <p style={{ margin: '0 6px' }}>Nuevo horario</p>
            </Button>
          </div>
        </div>
      </div>
      </Modal>

      <Dialog 
        open={showConfirmModal} 
        onClose={() => !isSaving && setShowConfirmModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>¿Deseas modificar un día en específico?</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Button
              fullWidth
              variant={applyToAllDays ? "contained" : "outlined"}
              onClick={() => setApplyToAllDays(true)}
              sx={{ mb: 2 }}
              disabled={isSaving}
            >
              NO - Aplicar cambios a todos los días seleccionados
            </Button>
            
            <Button
              fullWidth
              variant={!applyToAllDays ? "contained" : "outlined"}
              onClick={() => setApplyToAllDays(false)}
              sx={{ mb: 2 }}
              disabled={isSaving}
            >
              SÍ - Aplicar solo a un día específico
            </Button>

            {!applyToAllDays && editingIndex !== null && (
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Seleccione un día</InputLabel>
                <Select
                  value={selectedDay || ''}
                  onChange={(e) => setSelectedDay(Number(e.target.value))}
                  label="Seleccione un día"
                  disabled={isSaving}
                >
                  {Array.isArray(groupedTemplates[editingIndex]?.days) ? (
                    groupedTemplates[editingIndex].days.map((day: number) => (
                      <MenuItem key={day} value={day}>
                        {dayMap[day]}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value={groupedTemplates[editingIndex]?.days}>
                      {dayMap[groupedTemplates[editingIndex]?.days]}
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setShowConfirmModal(false);
              setApplyToAllDays(true);
              setSelectedDay(null);
            }}
            disabled={isSaving}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleConfirmSave} 
            variant="contained" 
            color="primary"
            disabled={isSaving || (!applyToAllDays && selectedDay === null)}
            startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isSaving ? 'Guardando...' : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ModalDetailedClass;
